import { useState, useEffect } from 'react';
import { useQuery, useMutation } from 'react-query';
import type { AxiosResponse } from 'axios';
import type { UseQueryResult, UseMutationResult } from 'react-query';
import {
  getPinnedPages,
  addPinnedPage,
  removePinnedPage,
  updatePinnedPage,
  searchPinnedPage,
} from '@hotjar/heatmaps';
import { noop } from '@hotjar/webapp/lib/function';
import { queryClient } from '@hotjar/webapp/utils/react-query';

export const QUERY_KEYS = {
  PINNED_PAGES: 'PinnedPages',
  SEARCH: 'SearchPinnedPage',
};

export const QUERY_STALE_TIME = 60 * 1000; // Mark queries stale after 1min

interface SearchPinnedPageParams {
  queryKey: [string, HeatmapAPI.WithSiteId & HeatmapAPI.UnsavedPinnedPage];
}

interface PinnedPagesAPIFunctions {
  activePinInReport: HeatmapAPI.PinnedPage | null;
  addPin: UseMutationResult<
    HeatmapAPI.PinnedPage,
    Error,
    HeatmapAPI.SavablePinnedPage & HeatmapAPI.WithSiteId
  >;
  removePin: UseMutationResult<AxiosResponse, Error, HeatmapAPI.PinnedPage & HeatmapAPI.WithSiteId>;
  updatePin: UseMutationResult<
    HeatmapAPI.PinnedPage,
    Error,
    HeatmapAPI.PinnedPage & HeatmapAPI.WithSiteId
  >;
  getAllPins: UseQueryResult<HeatmapAPI.PinnedPages, Error>;
  searchPins: UseQueryResult<HeatmapAPI.PinnedPages, Error>;
}

interface PinnedPagesAPIHookParams
  extends Partial<HeatmapAPI.UnsavedPinnedPage>,
    HeatmapAPI.WithSiteId {
  onSearchPinsError?: () => void;
  onGetAllPinsError?: () => void;
}

const getActivePinFromCache = (
  searchData: Partial<HeatmapAPI.UnsavedPinnedPage> & HeatmapAPI.WithSiteId,
): HeatmapAPI.PinnedPage[] | undefined =>
  queryClient.getQueryData<HeatmapAPI.PinnedPage[]>([QUERY_KEYS.SEARCH, searchData], {
    exact: true,
  });

const pickPinOrDefaultFromCacheResult = (
  cacheResult: HeatmapAPI.PinnedPage[] | undefined,
): HeatmapAPI.PinnedPage | null => cacheResult?.[0] || null;

export const usePinnedPages = ({
  onSearchPinsError = noop,
  onGetAllPinsError = noop,
  ...searchData
}: PinnedPagesAPIHookParams): PinnedPagesAPIFunctions => {
  const { siteId } = searchData;

  const activePinFromCache = pickPinOrDefaultFromCacheResult(getActivePinFromCache(searchData));

  const [activePinInReport, setActivePinInReport] = useState<HeatmapAPI.PinnedPage | null>(
    activePinFromCache,
  );

  useEffect(() => {
    setActivePinInReport(pickPinOrDefaultFromCacheResult(getActivePinFromCache(searchData)));
  }, [searchData, activePinFromCache]);

  const addPin = useMutation<
    HeatmapAPI.PinnedPage,
    Error,
    HeatmapAPI.SavablePinnedPage & HeatmapAPI.WithSiteId
  >(
    (newPinData: HeatmapAPI.SavablePinnedPage & HeatmapAPI.WithSiteId) => addPinnedPage(newPinData),
    {
      onSuccess: (data) => {
        const { pattern, matchOperation, jsonQueryString } = data;
        queryClient.refetchQueries([QUERY_KEYS.PINNED_PAGES, { siteId }], {
          exact: true,
          active: true,
        });
        queryClient.setQueryData(
          [QUERY_KEYS.SEARCH, { siteId, pattern, matchOperation, jsonQueryString }],
          [data],
        );
        setActivePinInReport(pickPinOrDefaultFromCacheResult([data]));
      },
    },
  );

  const removePin = useMutation<
    AxiosResponse,
    Error,
    HeatmapAPI.PinnedPage & HeatmapAPI.WithSiteId
  >((pinData: HeatmapAPI.PinnedPage & HeatmapAPI.WithSiteId) => removePinnedPage(pinData), {
    onSuccess: (data, variables) => {
      const { pattern, matchOperation, jsonQueryString } = variables;
      queryClient.removeQueries(
        [QUERY_KEYS.SEARCH, { siteId, pattern, matchOperation, jsonQueryString }],
        {
          exact: true,
        },
      );
      queryClient.refetchQueries([QUERY_KEYS.PINNED_PAGES, { siteId }], {
        exact: true,
        active: true,
      });
    },
  });

  const updatePin = useMutation<
    HeatmapAPI.PinnedPage,
    Error,
    HeatmapAPI.PinnedPage & HeatmapAPI.WithSiteId
  >((pinData: HeatmapAPI.PinnedPage & HeatmapAPI.WithSiteId) => updatePinnedPage(pinData), {
    onSuccess: (data) => {
      const { pattern, matchOperation, jsonQueryString } = data;
      queryClient.setQueryData(
        [QUERY_KEYS.SEARCH, { siteId, pattern, matchOperation, jsonQueryString }],
        [data],
      );
      queryClient.refetchQueries([QUERY_KEYS.PINNED_PAGES, { siteId }], {
        exact: true,
        active: true,
      });
    },
  });

  const getAllPins = useQuery<HeatmapAPI.PinnedPages, Error>(
    [QUERY_KEYS.PINNED_PAGES, { siteId }],
    (): Promise<HeatmapAPI.PinnedPages> => getPinnedPages({ siteId }),
    {
      enabled: Boolean(searchData.siteId),
      staleTime: QUERY_STALE_TIME,
      // Needed to allow immediate feedback to user
      // if retries allowed, query stays in loading for a while and error state is not triggered
      // And, error state not triggering could block user from reaching heatmaps quickly
      retry: false,
      refetchOnWindowFocus: false,
      onError: onGetAllPinsError,
      onSuccess: (data) => {
        data.forEach((item) => {
          const { pattern, matchOperation, jsonQueryString } = item;
          queryClient.setQueryData(
            [QUERY_KEYS.SEARCH, { siteId, pattern, matchOperation, jsonQueryString }],
            // This HAS to be an array as searchPin api responds with an array of pins for the matching parameters
            [item],
          );
        });
      },
    },
  );

  const searchPins = useQuery<HeatmapAPI.PinnedPages, Error>(
    [QUERY_KEYS.SEARCH, searchData],
    ({ queryKey }: SearchPinnedPageParams): Promise<HeatmapAPI.PinnedPages> => {
      const [, params] = queryKey;
      return searchPinnedPage(params);
    },
    {
      enabled: Boolean(
        searchData.siteId &&
          searchData.matchOperation &&
          searchData.pattern &&
          searchData.jsonQueryString,
      ),
      refetchOnMount: 'always', // considers cache but still refetch on mount
      refetchOnWindowFocus: false,
      staleTime: QUERY_STALE_TIME,
      onError: onSearchPinsError,
      initialData: getActivePinFromCache(searchData),
      onSuccess: (data) => {
        setActivePinInReport(pickPinOrDefaultFromCacheResult(data));
      },
    },
  );

  return {
    addPin,
    removePin,
    updatePin,
    getAllPins,
    searchPins,
    activePinInReport,
  };
};
